/**
 * Public (unauthenticated) routes — landing, legal pages, auth pages,
 * tenant onboarding, and the public clinic directory.
 *
 * Imported as a fragment by App.js so React Router still treats each
 * <Route> as a direct child of <Routes>.
 */
import React from 'react';
import { Route, Navigate } from 'react-router-dom';
import Landing from '../pages/Landing';
import ClinicianProfile from '../pages/ClinicianProfile';
import ClinicianList from '../pages/ClinicianList';
import Legal, { GDPRRights, HIPAASafeguards, Privacy, CopyrightPage, GlobalCompliance } from '../pages/legal/Legal';
import { Login, AdminLogin, PatientLogin, Register, AuthCallback, AcceptInvite } from '../pages/AuthPages';
import { ForgotPassword, ResetPassword } from '../pages/PasswordReset';
import WaitlistResponse from '../pages/WaitlistResponse';
import ClinicOnboard from '../pages/ClinicOnboard';
import OnboardMagic from '../pages/OnboardMagic';
import ClinicDirectory from '../pages/ClinicDirectory';
import PublicClinic from '../pages/PublicClinic';
import { Help } from '../pages/OpsManualAndFaq';
import SharedProgressView from '../pages/SharedProgressView';
import PublicGpReferral from '../pages/PublicGpReferral';
import PublicGpPreferences from '../pages/PublicGpPreferences';
import HeroSamples from '../pages/HeroSamples';
import AboutUs from '../pages/AboutUs';
import AlliedHealthcarePlatform from '../pages/AlliedHealthcarePlatform';

export const publicRoutes = (
  <>
    <Route path="/" element={<Landing />} />
    <Route path="/hero-samples" element={<HeroSamples />} />
    {/* Iter 127 — About Us dropdown pages. */}
    <Route path="/about" element={<AboutUs />} />
    <Route path="/platform" element={<AlliedHealthcarePlatform />} />
    <Route path="/p/:slug" element={<ClinicianProfile />} />
    <Route path="/clinicians" element={<ClinicianList />} />
    <Route path="/legal" element={<Legal />} />
    <Route path="/legal/gdpr" element={<GDPRRights />} />
    <Route path="/legal/hipaa" element={<HIPAASafeguards />} />
    <Route path="/legal/global" element={<GlobalCompliance />} />
    <Route path="/legal/privacy" element={<Privacy />} />
    <Route path="/legal/copyright" element={<CopyrightPage />} />
    {/* Iter 122 — /sign-in is the canonical universal auth route. /login
        and /staff-login are legacy aliases that redirect (301-style via
        <Navigate replace>) to /sign-in, so old bookmarks / emails still work. */}
    <Route path="/sign-in" element={<Login />} />
    <Route path="/login" element={<Navigate to="/sign-in" replace />} />
    <Route path="/staff-login" element={<Navigate to="/sign-in" replace />} />
    <Route path="/admin-login" element={<AdminLogin />} />
    <Route path="/patient-login" element={<PatientLogin />} />
    <Route path="/register" element={<Register />} />
    <Route path="/register/:code" element={<Register />} />
    <Route path="/signup" element={<Register />} />
    <Route path="/signup/:code" element={<Register />} />
    <Route path="/forgot-password" element={<ForgotPassword />} />
    <Route path="/reset-password/:token" element={<ResetPassword />} />
    <Route path="/waitlist/respond/:token/:action" element={<WaitlistResponse />} />
    <Route path="/accept-invite/:code" element={<AcceptInvite />} />
    <Route path="/auth-callback" element={<AuthCallback />} />
    <Route path="/start-clinic" element={<ClinicOnboard />} />
    <Route path="/onboard/magic/:token" element={<OnboardMagic />} />
    <Route path="/clinics" element={<ClinicDirectory />} />
    <Route path="/c/:slug" element={<PublicClinic />} />
    <Route path="/help" element={<Help />} />
    <Route path="/faq" element={<Help />} />
    {/* Iter 74 — public tokenised read-only patient progress link */}
    <Route path="/share/progress/:token" element={<SharedProgressView />} />
    {/* Iter 77 — public GP inbound-referral landing (linked from outcomes-30day email) */}
    <Route path="/refer/:code" element={<PublicGpReferral />} />
    {/* Iter 83 — public GP notification preferences */}
    <Route path="/gp/preferences/:code" element={<PublicGpPreferences />} />
  </>
);
